import { Container, Sprite, Texture } from 'pixi.js';
import { PixiOverlayHost } from '../PixiOverlayHost';
import { clampFraction, type IPlateBar, type PlateBarStyle } from './PlateBar';

/**
 * PixiPlateBar — IPlateBar as a Container of three sprites on Pixi's shared
 * 1×1 `Texture.WHITE`: outline (behind, 1 border width larger each side),
 * track and fill, each sized by scale and coloured by tint. The texture never
 * changes, so no update after construction is a structural change to the
 * stage (plan 13 §12; see PlateBar.ts). Same lazy build as PixiGraphics:
 * calls before `ensureReady()` resolves are kept and applied on build.
 */
export class PixiPlateBar implements IPlateBar {
  private root: Container | null = null;
  private fill: Sprite | null = null;
  private track: Sprite | null = null;
  private outline: Sprite | null = null;
  private destroyed = false;

  private x = 0;
  private y = 0;
  private width: number;
  private fraction = 1;
  private color = 0xffffff;
  // Hidden until first shown: nothing flashes at the world origin before the
  // owner's first update positions it.
  private shown = false;
  private strength = 1;

  constructor(private readonly style: PlateBarStyle) {
    this.width = style.width;
    void PixiOverlayHost.ensureReady().then(() => {
      if (this.destroyed) return;
      const root = new Container();
      root.zIndex = style.depth;
      const bw = style.borderWidth;
      const outline = bw > 0 ? solid(style.borderColor, style.borderAlpha) : null;
      if (outline) outline.position.set(-bw, -bw);
      const track = solid(style.bgColor, style.bgAlpha);
      const fill = solid(this.color, 1);
      if (outline) root.addChild(outline);
      root.addChild(track, fill);
      this.root = root;
      this.outline = outline;
      this.track = track;
      this.fill = fill;
      this.applyPosition();
      this.applyWidth();
      this.applyFill();
      root.alpha = this.shown ? this.strength : 0;
      PixiOverlayHost.addWorldChild(root);
    });
  }

  setPosition(x: number, y: number): void {
    this.x = x;
    this.y = y;
    this.applyPosition();
  }

  setFill(fraction: number, color: number): void {
    this.fraction = clampFraction(fraction);
    this.color = color;
    this.applyFill();
  }

  setWidth(width: number): void {
    if (width === this.width) return;
    this.width = width;
    this.applyWidth();
    this.applyFill();
  }

  setShown(shown: boolean): void {
    this.shown = shown;
    if (this.root) this.root.alpha = shown ? this.strength : 0;
  }

  setAlpha(alpha: number): void {
    this.strength = Math.max(0, Math.min(1, Number.isFinite(alpha) ? alpha : 1));
    if (this.root && this.shown) this.root.alpha = this.strength;
  }

  destroy(): void {
    this.destroyed = true;
    this.root?.destroy({ children: true });
    this.root = this.fill = this.track = this.outline = null;
  }

  private applyPosition(): void {
    this.root?.position.set(this.x, this.y);
  }

  private applyWidth(): void {
    const { height, borderWidth } = this.style;
    this.track?.scale.set(this.width, height);
    this.outline?.scale.set(this.width + borderWidth * 2, height + borderWidth * 2);
  }

  private applyFill(): void {
    if (!this.fill) return;
    this.fill.scale.set(this.width * this.fraction, this.style.height);
    this.fill.tint = this.color;
  }
}

/** A 1×1 white sprite: scale is its size in px, tint its colour. */
function solid(color: number, alpha: number): Sprite {
  const s = new Sprite(Texture.WHITE);
  s.tint = color;
  s.alpha = alpha;
  return s;
}
